import * as React from 'react';
import Row from 'react-bootstrap/Row';
import classnames from 'classnames';
import { find as _find } from 'lodash';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

export interface IExecutiveReportModalProps {
  show: boolean;
  onClose: (e: React.SyntheticEvent<any>) => void;
  onSubmit: (executiveReportRequest: IConfigExecutiveReportRequest) => void;
  executiveReport: IConfigExecutiveReportResponse;
  allReports: IConfigExecutiveReportResponse[];
  isCRM?: ConfigProductType;
}

interface IExecutiveReportModalState {
  executiveReport: IConfigExecutiveReportResponse;
  isEdit: boolean;
  error: {
    label: IFieldValidation;
    dashboardId: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errorMsgGen = new ErrorMessageGenerator();

class ExecutiveReportModal extends React.Component<IExecutiveReportModalProps, IExecutiveReportModalState> {
  constructor(props: IExecutiveReportModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  getEmptyState = (): IExecutiveReportModalState => ({
    executiveReport: {
      isActive: true,
      label: '',
      dashboardId: '',
    },
    isEdit: false,
    error: {
      label: {
        errorMessage: '',
        errorState: null,
      },
      dashboardId: {
        errorMessage: '',
        errorState: null,
      },
    },
  });

  componentDidUpdate(prevProps: IExecutiveReportModalProps) {
    const hasOpened = this.props.show !== prevProps.show && this.props.show;
    const isEdit = this.props.executiveReport;

    if (hasOpened && isEdit) {
      this.setState({
        executiveReport: this.props.executiveReport,
        error: this.getEmptyState().error,
        isEdit: true,
      });
    }

    if (hasOpened && !isEdit) {
      this.setState(this.getEmptyState());
    }
  }

  onSubmit = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (this.isValid()) {
      this.props.onSubmit(this.getRequestFromState());
    }
  };

  getRequestFromState = (): IConfigExecutiveReportRequest => {
    const { isActive, label, dashboardId, id, tempId } = this.state.executiveReport;

    const { isCRM } = this.props;

    return {
      id,
      tempId,
      ...(!id && !tempId ? { tempId: new Date().getTime() } : null),
      isActive,
      label: label.length ? label.trim() : null,
      dashboardId: dashboardId.length ? dashboardId.trim() : null,
      productType: isCRM ? ConfigProductType.CRM : ConfigProductType.XERCE,
    };
  };

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      executiveReport: {
        ...prevState.executiveReport,
        [name]: value,
      },
    }));
  };

  isValid = () => {
    const { executiveReport, isEdit } = this.state;
    let { allReports } = this.props;
    const error = this.getEmptyState().error;
    allReports = isEdit
      ? allReports.filter((report) =>
          executiveReport.id ? report.id !== executiveReport.id : report.tempId !== executiveReport.tempId
        )
      : allReports;
    let isValid = true;

    if (cyberdyneValidator.isEmptyString(executiveReport.label)) {
      error.label = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getMandatoryFieldMessage('Dashboard Name'),
      };

      isValid = false;
    } else if (!cyberdyneValidator.isStringLengthLimited(executiveReport.label, 1, 50)) {
      error.label.errorState = IValidationState.ERROR;
      error.label.errorMessage = errorMsgGen.getMinMaxCharacterLimitMessage('Dashboard name', 1, 50);
      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(executiveReport.dashboardId)) {
      error.dashboardId = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGen.getMandatoryFieldMessage('Dashboard Id'),
      };

      isValid = false;
    } else if (!cyberdyneValidator.isStringLengthLimited(executiveReport.dashboardId, 1, 500)) {
      error.dashboardId.errorState = IValidationState.ERROR;
      // tslint:disable-next-line: max-line-length
      error.dashboardId.errorMessage = errorMsgGen.getMinMaxCharacterLimitMessage('Dashboard ID', 1, 500);
      isValid = false;
    }

    if (!!_find(allReports, { dashboardId: executiveReport.dashboardId.trim() })) {
      error.dashboardId.errorState = IValidationState.ERROR;
      error.dashboardId.errorMessage = 'Dashboard ID already exists';
      isValid = false;
    }

    this.setState({
      error,
    });

    return isValid;
  };

  renderTitle = () => (this.props.executiveReport ? `Edit ${this.props.executiveReport.label}` : 'Add a QS Report');

  renderBody = () => {
    const dataTicketStatusOptions = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ];

    const { executiveReport, error } = this.state;

    return (
      <div className={classnames('integration__body')}>
        <Row>
          <Input
            width={4}
            field={{
              label: 'Dashboard Name',
              type: CustomFieldType.TEXT,
              value: executiveReport.label,
              isRequired: true,
            }}
            name="label"
            valueLabelOptions={dataTicketStatusOptions}
            onChange={this.handleChange}
            error={error.label}
          />
          <Input
            width={4}
            field={{
              label: 'Dashboard ID',
              type: CustomFieldType.TEXT,
              value: executiveReport.dashboardId,
              isRequired: true,
            }}
            error={error.dashboardId}
            name="dashboardId"
            valueLabelOptions={dataTicketStatusOptions}
            onChange={this.handleChange}
          />
        </Row>
        <Row>
          <Input
            field={{
              label: 'Status',
              type: OtherFieldType.RADIOBUTTON,
              isRequired: true,
              value: executiveReport.isActive,
            }}
            width={4}
            name="isActive"
            onChange={this.handleChange}
            valueLabelOptions={[
              {
                label: 'Active',
                value: true,
              },
              {
                label: 'Inactive',
                value: false,
              },
            ]}
          />
        </Row>
      </div>
    );
  };

  renderFooter = () => {
    const submitBtnTxt = this.state.isEdit ? 'Save' : 'Add Report';

    return (
      <div className={classnames('integration__footer')}>
        <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.props.onClose} content="Cancel" />
        <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content={submitBtnTxt} />
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('integration-modal')}
      />
    );
  }
}

export default ExecutiveReportModal;
